import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Radio, UserPlus, UserCheck, Crown, Music4, Loader2, Share2, Trophy } from "lucide-react";
import { toast } from "sonner";

const API = `${BACKEND_URL}/api`;

export default function ProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [weekStats, setWeekStats] = useState(null);

  const load = () => {
    axios.get(`${API}/users/${id}`)
      .then(r => setProfile(r.data))
      .catch(e => setError(e.response?.data?.detail || "User not found"));
    axios.get(`${API}/users/${id}/leaderboard-week`)
      .then(r => setWeekStats(r.data))
      .catch(() => {});
  };
  useEffect(load, [id]);

  const toggleFollow = async () => {
    if (!user) { toast.error("Log in to follow"); return; }
    setBusy(true);
    try {
      if (profile.is_following) await axios.delete(`${API}/users/${id}/follow`);
      else await axios.post(`${API}/users/${id}/follow`);
      load();
      toast.success(profile.is_following ? "Unfollowed" : "Following");
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  const fmt = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

  if (error) return (
    <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center">
      <div className="text-center space-y-4"><div className="font-display font-black text-3xl">{error}</div><Link to="/" className="text-[#00F0FF] font-black uppercase tracking-widest">Back to NeuralDeck</Link></div>
    </div>
  );
  if (!profile) return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]"/></div>;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="profile-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center"><Radio className="w-5 h-5 text-black" strokeWidth={2.5}/></div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#00F0FF]">DECK</span></div>
        </Link>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-start justify-between gap-6">
          <div className="space-y-2">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF]">// DJ Profile</div>
            <h1 className="font-display font-black text-5xl leading-tight flex items-center gap-3" data-testid="profile-name">
              {profile.display_name}
              {profile.is_premium && <span className="text-xs font-mono bg-[#00F0FF] text-black px-2 py-1 rounded uppercase tracking-widest flex items-center gap-1"><Crown className="w-3 h-3"/> PRO</span>}
            </h1>
            <div className="text-[#94A3B8] font-mono text-xs uppercase tracking-widest">
              <span data-testid="profile-followers"><b className="text-white">{profile.follower_count}</b> followers</span>
              <span className="mx-2">·</span>
              <span data-testid="profile-following"><b className="text-white">{profile.following_count}</b> following</span>
              <span className="mx-2">·</span>
              <span><b className="text-white">{profile.sets.length}</b> sets</span>
            </div>
          </div>
          {!profile.is_me && user && (
            <Button data-testid="profile-follow-button" onClick={toggleFollow} disabled={busy}
              className={profile.is_following
                ? "bg-transparent border border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-black font-black uppercase tracking-widest"
                : "bg-[#FF2A6D] text-white hover:bg-[#FF5588] font-black uppercase tracking-widest"}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : profile.is_following ? <><UserCheck className="w-4 h-4 mr-1.5"/> Following</> : <><UserPlus className="w-4 h-4 mr-1.5"/> Follow</>}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button data-testid="profile-share-button" onClick={async () => {
            const url = `${window.location.origin}/u/${profile.id}`;
            const text = `Check out ${profile.display_name} on NeuralDeck`;
            if (navigator.share) {
              try { await navigator.share({ title: profile.display_name, text, url }); return; }
              catch { /* fallback to clipboard */ }
            }
            try { await navigator.clipboard.writeText(url); toast.success("Profile link copied."); }
            catch { toast.error("Copy failed"); }
          }} variant="outline"
            className="border-[#2A354B] hover:border-[#00F0FF] bg-transparent text-white hover:text-[#00F0FF] font-black uppercase tracking-widest text-xs">
            <Share2 className="w-4 h-4 mr-1.5"/> Share Profile
          </Button>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${profile.display_name} on NeuralDeck`)}&url=${encodeURIComponent(`${window.location.origin}/u/${profile.id}`)}`}
             target="_blank" rel="noopener noreferrer" data-testid="profile-share-twitter"
             className="px-3 py-2 rounded border border-[#2A354B] hover:border-[#1DA1F2] text-xs font-black uppercase tracking-widest text-[#94A3B8] hover:text-[#1DA1F2]">
            X / Twitter
          </a>
        </div>

        {weekStats && weekStats.total_cents > 0 && (
          <Link to="/leaderboard" data-testid="profile-week-panel"
            className="block rounded-xl border border-[#FFC107]/40 bg-gradient-to-br from-[#FFC107]/10 to-[#FF2A6D]/5 p-5 hover:border-[#FFC107] transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#FFC107]/20 border-2 border-[#FFC107] flex items-center justify-center">
                <Trophy className="w-6 h-6 text-[#FFC107]"/>
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC107]">// This Week</div>
                <div className="font-display font-black text-2xl flex items-baseline gap-3 flex-wrap">
                  <span aria-label={`This week total ${(weekStats.total_cents / 100).toFixed(2)} dollars`}>${(weekStats.total_cents / 100).toFixed(2)}</span>
                  {weekStats.rank && <span className="text-base text-[#FFC107]" aria-label={`Rank ${weekStats.rank} of ${weekStats.of}`}>#{weekStats.rank} of {weekStats.of}</span>}
                </div>
                <div className="text-xs text-[#94A3B8]">
                  Tips ${(weekStats.tips_cents / 100).toFixed(2)} · Merch ${(weekStats.merch_cents / 100).toFixed(2)} · {weekStats.tx_count} tx
                </div>
              </div>
              <div className="text-xs font-mono uppercase tracking-widest text-[#94A3B8]">See board →</div>
            </div>
          </Link>
        )}

        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#00F0FF] mb-3 flex items-center gap-2">
            <Music4 className="w-3 h-3"/> Sets ({profile.sets.length})
          </div>
          {profile.sets.length === 0 && (
            <div className="text-sm text-[#94A3B8]">No sets published yet.</div>
          )}
          <div className="space-y-2">
            {profile.sets.map(s => (
              <Link key={s.id} to={`/set/${s.id}`} data-testid={`profile-set-${s.id}`}
                className="flex items-center gap-3 p-4 rounded-lg border border-[#1E2638] bg-[#121620] hover:border-[#00F0FF] hover:bg-[#00F0FF]/5 transition-colors">
                <div className="w-12 h-12 rounded bg-gradient-to-br from-[#00F0FF] to-[#FF2A6D] flex items-center justify-center flex-shrink-0">
                  <Music4 className="w-5 h-5 text-black"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{s.name}</div>
                  <div className="text-xs text-[#94A3B8]">{fmt(s.duration_seconds)} · {s.play_count} plays · {(s.tracklist || []).length} tracks · {new Date(s.created_at).toLocaleDateString()}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
